/**
 * Application route constants
 * Centralized route definitions to prevent magic strings
 */

import { APP_NAME } from '@/config/constants';

export const ROUTES = {
  // Public routes
  HOME: '/',
  PRODUCTS: '/products',
  PRODUCT_DETAIL: '/products/:slug',
  CART: '/cart',
  CHECKOUT: '/checkout',
  ORDER_SUCCESS: '/order-success/:orderId',
  
  // Auth routes
  LOGIN: '/login',
  TRACK_ORDER: '/track-order',
  
  // Protected routes (require auth)
  PROFILE: '/profile',
  ORDERS: '/orders',
  ORDER_DETAIL: '/orders/:orderId',
  WISHLIST: '/wishlist',
  ADDRESSES: '/addresses',
  
  // Admin routes (require admin role)
  ADMIN: '/admin',
  ADMIN_DASHBOARD: '/admin/dashboard',
  ADMIN_PRODUCTS: '/admin/products',
  ADMIN_PRODUCT_ADD: '/admin/products/add',
  ADMIN_PRODUCT_EDIT: '/admin/products/:id/edit',
  ADMIN_CATEGORIES: '/admin/categories',
  ADMIN_CATEGORY_ADD: '/admin/categories/add',
  ADMIN_CATEGORY_EDIT: '/admin/categories/:id/edit',
  ADMIN_BRANDS: '/admin/brands',
  ADMIN_BRAND_ADD: '/admin/brands/add',
  ADMIN_BRAND_EDIT: '/admin/brands/:id/edit',
  ADMIN_COUPONS: '/admin/coupons',
  ADMIN_COUPON_ADD: '/admin/coupons/add',
  ADMIN_COUPON_EDIT: '/admin/coupons/:id/edit',
  ADMIN_ORDERS: '/admin/orders',
  ADMIN_ORDER_DETAIL: '/admin/orders/:id',
  ADMIN_USERS: '/admin/users',
  ADMIN_SETTINGS: '/admin/settings',
  ADMIN_INQUIRIES: '/admin/inquiries',
  
  // Static pages
  BRANDS: '/brands',
  ABOUT: '/about',
  CONTACT: '/contact',
  PRIVACY: '/privacy-policy',
  TERMS: '/terms-of-service',
  SHIPPING: '/shipping-policy',
  RETURNS: '/returns-policy',
} as const;

export type RouteKey = keyof typeof ROUTES;
export type RoutePath = (typeof ROUTES)[RouteKey];

/**
 * Helper functions to generate dynamic routes
 */
export const generateRoute = {
  productDetail: (slug: string): string => `/products/${slug}`,
  orderDetail: (orderId: string): string => `/orders/${orderId}`,
  orderSuccess: (orderId: string): string => `/order-success/${orderId}`,
  adminProductEdit: (id: string): string => `/admin/products/${id}/edit`,
  adminCategoryEdit: (id: string): string => `/admin/categories/${id}/edit`,
  adminBrandEdit: (id: string): string => `/admin/brands/${id}/edit`,
  adminCouponEdit: (id: string): string => `/admin/coupons/${id}/edit`,
  adminOrderDetail: (id: string): string => `/admin/orders/${id}`,
};

/**
 * Route metadata for navigation and SEO
 */
export const ROUTE_META: Record<string, { title: string; description: string }> = {
  [ROUTES.HOME]: {
    title: `${APP_NAME} - Premium Dental Equipment`,
    description: 'Shop premium dental equipment and supplies for doctors and hospitals.',
  },
  [ROUTES.PRODUCTS]: {
    title: `Products - ${APP_NAME}`,
    description: 'Browse our complete catalog of dental equipment and supplies.',
  },
  [ROUTES.CART]: {
    title: `Shopping Cart - ${APP_NAME}`,
    description: 'Review your cart and proceed to checkout.',
  },
  [ROUTES.CHECKOUT]: {
    title: `Checkout - ${APP_NAME}`,
    description: 'Complete your order securely.',
  },
  [ROUTES.PROFILE]: {
    title: `My Profile - ${APP_NAME}`,
    description: 'Manage your account settings and preferences.',
  },
  [ROUTES.ORDERS]: {
    title: `My Orders - ${APP_NAME}`,
    description: 'View your order history and track shipments.',
  },
  [ROUTES.WISHLIST]: {
    title: `Wishlist - ${APP_NAME}`,
    description: 'Your saved products for later.',
  },
};
